import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';

// Round 10: Availability section inside the Employee Profile.
// Self-managed by default; managers can edit any employee's reductions
// (server logs the action to policy_override_events). Only tracks
// deviations from full availability — adding nothing is "fully available."
// ─── Time Off (Round 13) ──────────────────────────────────────────────────
// Two views in one tab:
//   - "Mine": staff sees own requests + submit form
//   - "Queue" (manager-only toggle): pending requests across the team
// Submission auto-computes the spec §8.2 deadline; late requests need
// a manager override at approve time, which writes to the policy
// override audit log.
// Read-only TOR list shown to managers viewing another employee's
// profile (W-106). Staff peers don't see this — server enforces.
export function ManagerTORHistory({ currentUser, targetUserId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const headers = { 'x-user-id': String(currentUser.id) };
  useEffect(() => {
    setLoading(true);
    api('GET', `/api/tor/by-user/${targetUserId}`, null, headers)
      .then((r) => setRows(r?.requests || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [targetUserId]);
  const stateBadge = (s) => {
    const colors = {
      pending:   { bg: '#fff8e1', fg: '#a16207' },
      approved:  { bg: '#e8f5e9', fg: '#2e7d32' },
      denied:    { bg: '#ffebee', fg: '#c62828' },
      cancelled: { bg: '#f5f5f5', fg: '#9e9e9e' },
    };
    const c = colors[s] || colors.cancelled;
    return (
      <span style={{ background: c.bg, color: c.fg, padding: '2px 8px',
                      borderRadius: 999, fontSize: 11, fontWeight: 700,
                      textTransform: 'capitalize' }}>{s}</span>
    );
  };
  return (
    <div>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#212121', marginBottom: 4 }}>
        Time off requests
      </div>
      <div style={{ fontSize: 11, color: '#9e9e9e', marginBottom: 8 }}>
        Staff submit requests from their own Schedule → Time off page. You approve from Schedule → Time off → Approve.
      </div>
      {loading ? (
        <div style={{ fontSize: 12, color: '#9e9e9e' }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div style={{ fontSize: 12, color: '#9e9e9e', fontStyle: 'italic' }}>
          No time-off requests on file.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {rows.slice(0, 10).map((r) => {
            const sameDay = r.requested_date_start === r.requested_date_end;
            const dateStr = sameDay ? r.requested_date_start
                                    : `${r.requested_date_start} → ${r.requested_date_end}`;
            const partial = r.requested_time_start && r.requested_time_end
                          ? ` · ${r.requested_time_start}–${r.requested_time_end}`
                          : '';
            return (
              <div key={r.id} style={{ border: '1px solid #e0e0e0', borderRadius: 6, padding: 10,
                                          display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, fontSize: 13 }}>
                <div>
                  <div style={{ color: '#37474f' }}>{dateStr}{partial}</div>
                  <div style={{ fontSize: 11, color: '#9e9e9e' }}>
                    {r.use_pto ? `PTO ${r.pto_hours}h · ` : ''}
                    {r.reason ? `"${r.reason}"` : 'no reason given'}
                  </div>
                </div>
                {stateBadge(r.state)}
              </div>
            );
          })}
          {rows.length > 10 && (
            <div style={{ fontSize: 11, color: '#9e9e9e', fontStyle: 'italic' }}>
              Showing 10 most recent of {rows.length}.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
